/** Índice del manual (/manual): cada sección es su módulo en ./secciones, con el frontmatter
 * `covers:`/`revisado:` que vigilan scripts/gates/check_manual_freshness.py y el check pagina_sin_manual. */
import type {EnCorto, ManualSeccion, ManualSectionId} from './types'
import {seccion as crearTuEmpresa} from './secciones/crear-tu-empresa'
import {seccion as entrarYMoverte} from './secciones/entrar-y-moverte'
import {seccion as perfilDeEmpresa} from './secciones/perfil-de-empresa'
import {seccion as organigramaYCargos} from './secciones/organigrama-y-cargos'
import {seccion as empleados} from './secciones/empleados'
import {seccion as proveedores} from './secciones/proveedores'
import {seccion as obrasYProyectos} from './secciones/obras-y-proyectos'
import {seccion as autoevaluacion0312} from './secciones/autoevaluacion-0312'
import {seccion as evidenciasYSoportes} from './secciones/evidencias-y-soportes'
import {seccion as matrizLegal} from './secciones/matriz-legal'
import {seccion as matrizDeRiesgos} from './secciones/matriz-de-riesgos'
import {seccion as planDeTrabajoYMejoramiento} from './secciones/plan-de-trabajo-y-mejoramiento'
import {seccion as incidentesEIncapacidades} from './secciones/incidentes-e-incapacidades'
import {seccion as certificacionYAuditoria} from './secciones/certificacion-y-auditoria'
import {seccion as tuRegistro} from './secciones/tu-registro'
import {seccion as entrarAKairos} from './secciones/entrar-a-kairos'
import {seccion as tuPerfilSociodemografico} from './secciones/tu-perfil-sociodemografico'
import {seccion as tuEntrevistaYTusTareas} from './secciones/tu-entrevista-y-tus-tareas'
import {seccion as tuPanel} from './secciones/tu-panel'
import {seccion as planYFacturacion} from './secciones/plan-y-facturacion'

export type {ManualSeccion, ManualSectionId}

export type ManualGrupo = {
  id: string
  titulo: string
  /** Para quién es el capítulo; se muestra bajo su título en el índice. */
  descripcion?: string
  secciones: ManualSeccion[]
}

export const MANUAL_GRUPOS: ManualGrupo[] = [
  {
    id: 'primeros-pasos',
    titulo: 'Primeros pasos',
    descripcion: 'Si vas a crear la empresa en Kairos: el registro inicial y cómo está organizada la aplicación.',
    secciones: [crearTuEmpresa, entrarYMoverte],
  },
  {
    id: 'si-eres-trabajador',
    titulo: 'Si eres trabajador',
    descripcion:
      'Tu guía si tu empresa ya usa Kairos y te compartieron un enlace: cómo te registras, cómo entras con tu cédula, y lo único que te toca hacer adentro.',
    secciones: [tuRegistro, entrarAKairos, tuPerfilSociodemografico, tuEntrevistaYTusTareas],
  },
  {
    id: 'tu-empresa',
    titulo: 'Tu empresa',
    descripcion: 'Los datos con los que Kairos entiende a tu empresa: perfil, estructura, personas y contratistas.',
    secciones: [perfilDeEmpresa, organigramaYCargos, empleados, proveedores, obrasYProyectos],
  },
  {
    id: 'tu-sgsst',
    titulo: 'Tu SG-SST',
    descripcion: 'El sistema de gestión en sí: autoevaluación, soportes, matrices, plan de trabajo y certificación.',
    secciones: [
      autoevaluacion0312,
      evidenciasYSoportes,
      matrizLegal,
      matrizDeRiesgos,
      planDeTrabajoYMejoramiento,
      incidentesEIncapacidades,
      certificacionYAuditoria,
    ],
  },
  {
    id: 'plan-y-pagos',
    titulo: 'Plan y pagos',
    descripcion: 'Cualquier empresa en Kairos: qué incluye el plan pago, cómo se activa y cómo se paga.',
    secciones: [planYFacturacion],
  },
  {
    id: 'partners',
    titulo: 'Partners',
    descripcion: 'Solo para consultores SST externos que traen empresas cliente bajo su código.',
    secciones: [tuPanel],
  },
]

export function findSeccion(id: string): {grupo: ManualGrupo; seccion: ManualSeccion} | null {
  for (const grupo of MANUAL_GRUPOS) {
    const seccion = grupo.secciones.find((s) => s.id === id)
    if (seccion) return {grupo, seccion}
  }
  return null
}

/** Las pantallas sin capítulo propio caen en la que explica cómo está organizada la aplicación. */
const SECCION_GENERAL: ManualSectionId = 'entrar-y-moverte'

/** El «En corto» y el título van juntos, solo donde la ruta comparte sección con otra pantalla:
 * pisar solo las viñetas deja la hoja contradiciéndose. */
type EntradaRuta = readonly [string, ManualSectionId, EnCorto?, string?]

/** Se recorre en orden y gana la primera entrada que calza por segmento. */
const RUTA_A_SECCION: ReadonlyArray<EntradaRuta> = [
  // SG-SST: lo más profundo primero.
  ['sgsst/matriz-legal', 'matriz-legal'],
  ['sgsst/matriz-riesgos', 'matriz-de-riesgos'],
  ['sgsst/certificaciones', 'certificacion-y-auditoria'],
  ['sgsst/carpetas-visita', 'certificacion-y-auditoria'],
  [
    'sgsst/perfiles-cargo',
    'organigrama-y-cargos',
    [
      'Lo que exige cada cargo de su empresa',
      'Confirme el perfil o use la propuesta',
      'Mover personas de cargo, en Organigrama',
    ],
    // El título también se pisa: sin él la hoja abría «Organigrama y cargos» sobre estas viñetas.
    'Perfiles de cargo',
  ],
  ['sgsst/positions', 'organigrama-y-cargos'],
  ['sgsst/perfil-agregados', 'tu-perfil-sociodemografico'],
  ['sgsst/plan', 'plan-de-trabajo-y-mejoramiento'],
  ['sgsst/pendientes', 'evidencias-y-soportes'],
  ['sgsst/historial', 'autoevaluacion-0312'],
  ['sgsst', 'autoevaluacion-0312'],
  ['plan-trabajo', 'plan-de-trabajo-y-mejoramiento'],
  ['profile', 'perfil-de-empresa'],
  ['cifras', 'perfil-de-empresa'],
  ['perfil-riesgo', 'perfil-de-empresa'],
  [
    'organigrama',
    'organigrama-y-cargos',
    [
      'Áreas, cargos y quién reporta a quién',
      'Mueva personas; cree áreas y cargos',
      'El perfil del cargo vive en Perfiles',
    ],
  ],
  ['employees', 'empleados'],
  ['empleados', 'empleados'],
  [
    'examenes',
    'evidencias-y-soportes',
    [
      'Los exámenes ocupacionales de su gente',
      'Pida un examen y registre su concepto',
      'Cambios de cargo por decidir, en «Sugerencias»',
      'La IPS que lo hace, en IPS',
    ],
    'Exámenes ocupacionales',
  ],
  [
    'ips',
    'empleados',
    ['Las IPS que atienden a su empresa', 'Regístrelas y atestigüe su licencia', 'Los exámenes se piden en Exámenes'],
    'IPS',
  ],
  [
    'solicitudes-ips',
    'empleados',
    [
      'Los exámenes que pidió a una IPS',
      'Prepare cargo por cargo en «Preparar»',
      'Siga cada solicitud y su concepto',
      'Los plazos, en «Plazos»; la IPS, en IPS',
    ],
    'Solicitudes a la IPS',
  ],
  ['proveedores', 'proveedores'],
  ['obras', 'obras-y-proyectos'],
  ['incidentes', 'incidentes-e-incapacidades'],
  ['incapacidades', 'incidentes-e-incapacidades'],
  ['expediente', 'certificacion-y-auditoria'],
  ['plan', 'plan-y-facturacion'],
  ['mi/perfil-sociodemografico', 'tu-perfil-sociodemografico'],
  ['mi/informacion', 'tu-perfil-sociodemografico'],
  ['mi/ficha', 'tu-perfil-sociodemografico'],
  ['mi/cumplimiento', 'tu-entrevista-y-tus-tareas'],
  ['mi/acciones', 'tu-entrevista-y-tus-tareas'],
  ['mi/solicitudes', 'tu-entrevista-y-tus-tareas'],
  ['mi/soportes', 'tu-entrevista-y-tus-tareas'],
  ['mi/soportes-solicitudes', 'tu-entrevista-y-tus-tareas'],
  ['ruta', 'entrar-y-moverte'],
]

/** Rutas que no cuelgan de una empresa; se buscan sobre el pathname entero. */
const RUTA_SUELTA_A_SECCION: ReadonlyArray<EntradaRuta> = [
  ['/partner', 'tu-panel'],
  [
    '/notifications',
    'entrar-y-moverte',
    [
      'Todos sus avisos, con filtro por tipo',
      'Ábralos, márquelos leídos o bórrelos',
      'La campana solo muestra los últimos veinte',
    ],
    'Notificaciones',
  ],
  ['/registro/empresa', 'crear-tu-empresa'],
  ['/registro', 'tu-registro'],
  ['/claim', 'entrar-a-kairos'],
  ['/reset', 'entrar-a-kairos'],
  ['/reset-request', 'entrar-a-kairos'],
  ['/login', 'entrar-a-kairos'],
]

/** Por segmento: un `startsWith` pelado haría que `plan` se comiera a `plan-trabajo`. */
function calza(resto: string, patron: string): boolean {
  return resto === patron || resto.startsWith(`${patron}/`)
}

/** `enCorto` y `titulo` solo cuando la ruta los pisa; vacíos, mandan los de la sección. */
export type DestinoDeRuta = {seccion: ManualSectionId; enCorto?: EnCorto; titulo?: string}

export function seccionDeRuta(pathname: string): DestinoDeRuta {
  const empresa = pathname.match(/^\/businesses\/[^/]+\/?(.*)$/)
  if (empresa) {
    const resto = empresa[1]
    if (!resto) return {seccion: SECCION_GENERAL}
    for (const [patron, seccion, enCorto, titulo] of RUTA_A_SECCION) {
      if (calza(resto, patron)) return {seccion, enCorto, titulo}
    }
    return {seccion: SECCION_GENERAL}
  }
  for (const [patron, seccion, enCorto, titulo] of RUTA_SUELTA_A_SECCION) {
    if (pathname === patron || pathname.startsWith(`${patron}/`)) return {seccion, enCorto, titulo}
  }
  return {seccion: SECCION_GENERAL}
}
